/**
 * THE WORK'S DESK (owner, 2026-10-16: "I can see the meta information of the work … static … then my chat about
 * the work … to my left, what decision has been taken, what is pending, the team under this work, what we need to
 * do … it's not something I have to scroll many sections").
 *
 *   LEFT    where it stands: asked for · waiting on you · done, and who is on it. Each item opens in the panel.
 *   MIDDLE  the work, stated once at the top and kept in view; under it only the conversation scrolls, and the box
 *           to say the next thing stays at the foot (WorkConversation).
 *   PANEL   whatever was opened — a request with every way to answer it, a decision with its review, the full
 *           working, the evidence, the workpaper's versions, the history — beside the conversation, never instead
 *           of it. Closing it leaves the person where they were.
 *
 * The workpaper is a file to keep (Excel), downloaded from the header; it is not a page to scroll.
 *
 * ONE ANSWER, ALWAYS. Every act returns the whole work file as the server now projects it, and the screen replaces
 * what it holds with that. It never patches its copy.
 */
import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, X } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { WorkFileResponse, type WorkFile } from "@leveri/contracts";
import { FileLayout, Label, Marker, Notice, Reading, Spine, StateLedger } from "../../file/index.js";
import { call, failureMessage } from "../../api.js";
import { EvidenceSection } from "./evidence.js";
import { HistorySection } from "./history.js";
import { Conclusions, RequestCard, WorkSection } from "./workSection.js";
import { WorkpaperSection } from "./workpaper.js";
import { WorkConversation, type ConversationLinkTarget } from "./conversation.js";
import { AccountSetup } from "./accountSetup.js";

/** How often a WORKING file re-reads itself. */
const REFRESH_WHILE_WORKING_MS = 2000;

/** What the side panel holds. `at` is an element to bring into view inside it. */
type Panel =
  | { kind: "request"; id: string }
  | { kind: "decision"; id: string }
  | { kind: "decisions" }
  | { kind: "accounts" }
  | { kind: "working"; at?: string }
  | { kind: "evidence" }
  | { kind: "workpaper" }
  | { kind: "history" }
  | { kind: "lists" };

export function WorkFileScreen() {
  const { workId } = useParams<{ workId: string }>();
  const navigate = useNavigate();
  const [work, setWork] = useState<WorkFile | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [panel, setPanel] = useState<Panel | null>(null);
  /** A message to come back to in the conversation. */
  const [focus, setFocus] = useState<string | null>(null);
  /** What the last act actually did, in the server's own words. */
  const [outcome, setOutcome] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!workId) return;
    setFailure(null);
    try {
      const response = await call("GET", `/api/work/${workId}`, WorkFileResponse);
      setWork(response.work);
    } catch (error) {
      setFailure(failureMessage(error));
    }
  }, [workId]);

  useEffect(() => { void load(); }, [load]);
  // Another work: nothing opened on the last one stays open.
  useEffect(() => { setPanel(null); setFocus(null); setOutcome(null); }, [workId]);

  /**
   * ARRIVING WITH AN ADDRESS (R11 workspace queue, engagement lists, a shared link): a request or a decision opens in
   * the panel; a message is brought into view; anything else (#figures, #open, #established, #bridge …) opens the
   * full working at that place. Once per arrival, so it never fights the reader afterwards.
   */
  const { hash } = useLocation();
  const [arrivedAt, setArrivedAt] = useState<string | null>(null);
  useEffect(() => {
    if (!work || !hash || arrivedAt === `${work.id}${hash}`) return;
    const id = decodeURIComponent(hash.slice(1));
    if (id.startsWith("need-")) setPanel({ kind: "request", id: id.slice(5) });
    else if (id.startsWith("decision-")) setPanel({ kind: "decision", id: id.slice(9) });
    else if (id.startsWith("entry-") || id.startsWith("turn-")) setFocus(id);
    else setPanel({ kind: "working", at: id });
    setArrivedAt(`${work.id}${hash}`);
  }, [work, hash, arrivedAt]);

  /**
   * WORKING IS OBSERVABLE (R6.5). While the server says the work is WORKING — and no person is needed — the file
   * re-reads itself, quietly, until the server says something else. A refresh that fails keeps the last answer on
   * screen and tries again; it never blanks the page. Nothing here decides a state.
   */
  const working = work?.status === "WORKING" && !work.needsAttention;
  useEffect(() => {
    if (!working || !workId) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await call("GET", `/api/work/${workId}`, WorkFileResponse);
        if (!cancelled) setWork(response.work);
      } catch {
        if (!cancelled) setWork((current) => (current ? { ...current } : current));
      }
    }, REFRESH_WHILE_WORKING_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [working, workId, work]);

  /** Open what a message or a list names, in the panel beside the conversation. */
  const show = useCallback((to: ConversationLinkTarget) => {
    setFocus(null);
    if (to.id === "accounts") setPanel({ kind: "accounts" });
    else if (to.kind === "REQUEST") setPanel({ kind: "request", id: to.id });
    else if (to.kind === "DECISION") setPanel({ kind: "decision", id: to.id });
    else if (to.id === "workpaper") setPanel({ kind: "workpaper" });
    else if (to.id === "review") setPanel({ kind: "decisions" });
    else setPanel({ kind: "working", at: to.id });
  }, []);
  /** Back to the message something came from: the panel closes and the message is rung. */
  const showEntry = useCallback((entryId: string) => { setPanel(null); setFocus(`entry-${entryId}`); }, []);

  // Inside the panel, bring the named place into view once it has rendered.
  const at = panel?.kind === "working" ? panel.at ?? null : null;
  useEffect(() => {
    if (!at) return;
    const frame = window.requestAnimationFrame(() => document.getElementById(at)?.scrollIntoView({ block: "start" }));
    return () => window.cancelAnimationFrame(frame);
  }, [at]);

  const back = (
    <button
      type="button"
      onClick={() => navigate("/")}
      className="inline-flex items-center gap-1.5 text-[13px] text-stone transition-colors hover:text-ink"
    >
      <ArrowLeft size={14} /> Accounting work
    </button>
  );

  // Stable: children use it in effect dependencies (the suggestions request, asked once per version).
  const restate = useCallback((next: WorkFile) => setWork(next), []);
  if (failure) {
    return (
      <FileLayout spine={<Spine>{back}</Spine>}>
        <Notice>{failure}</Notice>
        <button type="button" onClick={() => void load()} className="mt-3 text-[13px] text-stone hover:text-ink">
          Try again
        </button>
      </FileLayout>
    );
  }
  if (!work) {
    return (
      <FileLayout spine={<Spine>{back}</Spine>}>
        <Reading>Opening the file…</Reading>
      </FileLayout>
    );
  }

  const standing = <Standing work={work} onOpen={show} onPanel={setPanel} />;

  return (
    // On a wide desk the panel takes its own column and the conversation narrows beside it; below that it slides over.
    <div className={`grid h-[calc(100vh-56px)] grid-cols-1 lg:grid-cols-[264px_minmax(0,1fr)] ${panel ? "xl:grid-cols-[264px_minmax(0,1fr)_minmax(480px,560px)]" : ""}`}>
      {/* LEFT: where the work stands, and who is on it. */}
      <nav aria-label="Where this work stands" className="hidden min-h-0 overflow-y-auto border-r border-rule bg-canvas px-6 py-6 lg:block">
        {back}
        <div className="mt-6">{standing}</div>
      </nav>

      {/* MIDDLE: the work stated once, and the conversation. */}
      <main className="min-h-0 min-w-0">
        <WorkConversation work={work} outcome={outcome} onWork={restate} onOutcome={setOutcome} onOpen={show} focus={focus} onLists={() => setPanel({ kind: "lists" })} />
      </main>

      {/* THE PANEL: what was opened, beside the conversation. */}
      {panel && (
        <aside aria-label="Opened from this work" className="fixed bottom-0 right-0 top-14 z-40 flex w-full max-w-[640px] flex-col border-l border-rule bg-canvas shadow-[-16px_0_32px_-16px_rgba(27,25,22,0.25)] xl:static xl:z-auto xl:min-h-0 xl:max-w-none xl:bg-raised xl:shadow-none" data-testid="work-panel">
          <div className="flex shrink-0 items-center justify-between border-b border-rule px-6 py-3">
            <span className="text-[11px] font-medium uppercase tracking-[0.09em] text-stone">{PANEL_TITLE[panel.kind]}</span>
            <button type="button" onClick={() => setPanel(null)} aria-label="Close" className="p-1 text-stone hover:text-ink" data-testid="work-panel-close"><X size={18} /></button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
            <PanelBody panel={panel} work={work} outcome={outcome} onWork={restate} onOutcome={setOutcome} onShowEntry={showEntry} onOpen={show} onPanel={setPanel} />
          </div>
        </aside>
      )}
    </div>
  );
}

const PANEL_TITLE: Record<Panel["kind"], string> = {
  request: "Asked for", decision: "A decision", decisions: "Waiting on you", accounts: "Set up accounts", working: "The full working",
  evidence: "Evidence", workpaper: "Workpaper", history: "History", lists: "Where it stands",
};

/** Left of the conversation (and, on a phone, in the panel): the three lists, who is on the work, and the rest. */
function Standing({ work, onOpen, onPanel }: { work: WorkFile; onOpen: (to: ConversationLinkTarget) => void; onPanel: (panel: Panel) => void }) {
  const tasks = work.tasks;
  return (
    <div className="flex flex-col gap-7">
      <TaskList label="Asked for" items={tasks.asked} empty="Nothing is asked for." tone="attention" onOpen={onOpen} testid="tasks-asked" />
      <TaskList label="Waiting on you" items={tasks.pending} empty="Nothing waits on you." tone="progress" onOpen={onOpen} testid="tasks-pending" />
      <TaskList label="Done" items={tasks.done} empty="Nothing is done yet." tone="established" onOpen={onOpen} testid="tasks-done" collapsed />
      <div data-testid="work-team">
        <Label className="mb-2.5">On this work</Label>
        <p className="text-[13px] text-ink">{work.assignee ? `Held by ${work.assignee.displayName}` : "Nobody holds it yet"}</p>
        {work.startedText && <p className="mt-0.5 text-[12px] text-stone">{work.startedText}</p>}
      </div>
      <div className="flex flex-col items-start gap-1.5 border-t border-rule pt-5 text-[13px]">
        <button type="button" onClick={() => onPanel({ kind: "working" })} className="text-stone hover:text-ink" data-testid="open-working">The full working</button>
        <button type="button" onClick={() => onPanel({ kind: "evidence" })} className="text-stone hover:text-ink">Evidence{work.records.length ? ` · ${work.records.length}` : ""}</button>
        {work.workpapers.length > 0 && <button type="button" onClick={() => onPanel({ kind: "workpaper" })} className="text-stone hover:text-ink">Workpaper versions · {work.workpapers.length}</button>}
        <button type="button" onClick={() => onPanel({ kind: "history" })} className="text-stone hover:text-ink">History{work.history.length ? ` · ${work.history.length}` : ""}</button>
      </div>
      {/* THE STATE LEDGER — the position, broken out, and computed on the server; folded: the band and the lists
          already say where the work stands. */}
      <details>
        <summary className="cursor-pointer text-[11px] font-medium uppercase tracking-[0.09em] text-stone">State</summary>
        <div className="mt-3"><StateLedger rows={work.stateLedger} /></div>
      </details>
    </div>
  );
}

/** What the panel shows: the one thing opened, with everything a person can do to it. */
function PanelBody({ panel, work, outcome, onWork, onOutcome, onShowEntry, onOpen, onPanel }: {
  panel: Panel; work: WorkFile; outcome: string | null;
  onWork: (work: WorkFile) => void; onOutcome: (outcome: string) => void; onShowEntry: (entryId: string) => void;
  onOpen: (to: ConversationLinkTarget) => void; onPanel: (panel: Panel) => void;
}) {
  // One review open at a time, as on the full working.
  const [reviewing, setReviewing] = useState<string | null>(null);
  const reviewProps = { work, reviewing, onReviewing: setReviewing, onWork, onOutcome };
  switch (panel.kind) {
    case "request": {
      const request = work.openRequests.find((r) => r.requirementId === panel.id);
      if (request) return <RequestCard work={work} request={request} onWork={onWork} onOutcome={onOutcome} onShowEntry={onShowEntry} />;
      // Answered meanwhile (or answered before): what was asked, and how it was answered.
      const done = work.tasks.done.find((t) => t.kind === "REQUEST" && t.id === panel.id);
      return done
        ? <div><p className="text-[16px] font-medium text-ink">{done.label}</p><p className="mt-1.5 text-sm text-ink-soft">{done.note}</p><p className="mt-1 text-[12px] text-stone">{done.atText}</p></div>
        : <p className="text-sm text-stone">This request is no longer open.</p>;
    }
    case "decision": {
      const row = work.conclusions.find((c) => c.id === panel.id);
      return row ? <Conclusions title={null} rows={[row]} {...reviewProps} /> : <p className="text-sm text-stone">This decision is no longer on the current version.</p>;
    }
    case "decisions": {
      const pending = work.conclusions.filter((c) => c.current && c.canReview && !c.review.act);
      return pending.length > 0 ? <Conclusions title={null} rows={pending} {...reviewProps} /> : <p className="text-sm text-stone">Nothing waits on you.</p>;
    }
    case "working": return <WorkSection work={work} outcome={outcome} onWork={onWork} onOutcome={onOutcome} onShowEntry={onShowEntry} bare />;
    case "evidence": return <EvidenceSection work={work} onWork={onWork} />;
    case "workpaper": return <WorkpaperSection work={work} />;
    case "history": return <HistorySection work={work} />;
    case "lists": return <Standing work={work} onOpen={onOpen} onPanel={onPanel} />;
    case "accounts": return <AccountSetup work={work} onWork={onWork} onOutcome={onOutcome} />;
  }
}

/** One of the spine's three lists: each item names what it is, when, and opens it. */
function TaskList({ label, items, empty, tone, onOpen, testid, collapsed }: {
  label: string;
  items: WorkFile["tasks"]["asked"];
  empty: string;
  tone: "attention" | "progress" | "established";
  onOpen: (target: ConversationLinkTarget) => void;
  testid: string;
  collapsed?: boolean;
}) {
  const [all, setAll] = useState(!collapsed);
  const shown = all ? items : items.slice(0, 3);
  return (
    <div data-testid={testid} className="min-w-[200px]">
      <Label className="mb-2.5">{label} <span className="ml-1 font-normal text-stone [font-variant-numeric:tabular-nums]">{items.length}</span></Label>
      {items.length === 0 ? <p className="text-[13px] text-stone">{empty}</p> : (
        <ul className="flex flex-col gap-2">
          {shown.map((item) => (
            <li key={`${item.kind}-${item.id}`}>
              <button type="button" onClick={() => onOpen({ kind: item.kind, id: item.id })} className="group flex w-full items-start gap-2 text-left">
                <span className="mt-[7px] shrink-0"><Marker tone={tone} /></span>
                <span className="min-w-0">
                  <span className="block text-[13px] leading-snug text-ink group-hover:underline">{item.label}</span>
                  <span className="block text-[12px] text-stone">{[item.note, item.atText].filter(Boolean).join(" · ")}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {!all && items.length > 3 && (
        <button type="button" onClick={() => setAll(true)} className="mt-2 text-[12px] text-stone hover:text-ink">All {items.length}</button>
      )}
    </div>
  );
}
